import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  BrainCircuit,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  LineChart,
  LockKeyhole,
  MessagesSquare,
  PlayCircle,
  Route,
  Sparkles,
  UserRoundCheck,
  UsersRound,
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import BottomContent from '../../components/BottomContent';
import ScrollButton from "../../components/ScrollButton";
import ContactButton from '../../components/ContactButton';
import Thank from '../../components/Thank';
import DarkModeButton from '../../components/DarkModeButton';
import EducationImg from '../../assets/img/EducationHero.png';
import VidyaLogo from '../../assets/logo/VidyaLogo.png';
import '../CustomStyle.css';
import './EducationalSystem.css';

const heroStats = [
  { value: '1:1', label: 'Adaptive learning' },
  { value: '24/7', label: 'AI tutor support' },
  { value: '360', label: 'Learning analytics' },
];

const capabilities = [
  {
    icon: Route,
    title: 'Adaptive Learning Paths',
    text: "Personalized sequencing adjusts content, practice, and pace around each learner's goals and mastery level.",
  },
  {
    icon: MessagesSquare,
    title: 'Conversational AI Tutor',
    text: 'Guided explanations, doubt resolution, and practice prompts help students stay engaged between classes.',
  },
  {
    icon: ClipboardCheck,
    title: 'Smart Assessment Engine',
    text: 'Automated quizzes, feedback, rubrics, and remediation flows reduce repetitive evaluation work.',
  },
  {
    icon: BarChart3,
    title: 'Educator Intelligence',
    text: 'Dashboards surface progress, engagement, risk areas, and intervention signals for faster decisions.',
  },
];

const learningSignals = [
  'Learner profile',
  'Skill map',
  'Content recommendation',
  'Tutor interaction',
  'Assessment feedback',
  'Teacher intervention',
];

const roles = [
  {
    icon: GraduationCap,
    title: 'Students',
    text: 'Clear next steps, contextual explanations, practice loops, and confidence-building feedback.',
  },
  {
    icon: UserRoundCheck,
    title: 'Educators',
    text: 'Early risk detection, assessment support, class-level insight, and targeted remediation planning.',
  },
  {
    icon: UsersRound,
    title: 'Institutions',
    text: 'Scalable AI learning workflows for schools, colleges, training academies, and digital platforms.',
  },
];

const outcomes = [
  'Personalized learning journeys',
  'Lower manual assessment effort',
  'Improved student engagement',
  'Actionable academic analytics',
  'AI-assisted teacher workflows',
  'Secure education data practices',
];

const workflow = [
  {
    icon: BrainCircuit,
    title: 'Understand',
    text: 'Vidya builds a learner context from goals, performance, activity, and concept-level readiness.',
  },
  {
    icon: BookOpenCheck,
    title: 'Guide',
    text: 'The platform recommends lessons, learning paths, tutor prompts, and practice sets for steady progress.',
  },
  {
    icon: LineChart,
    title: 'Improve',
    text: 'Educators receive insight into performance shifts, gaps, interventions, and assessment outcomes.',
  },
];

export default function EducationSystem() {
  return (
    <>
      <Navbar />

      <main className="vidya-page">
        <section className="vidya-hero" style={{ backgroundImage: `url(${EducationImg})` }}>
          <div className="vidya-hero-overlay"></div>
          <div className="container vidya-hero-inner">
            <div className="vidya-hero-copy">
              <div className="vidya-product-mark">
                <img src={VidyaLogo} alt="Vidya logo" />
                <span>Vidya Education AI</span>
              </div>
              <h1>Vidya - Applied AI in Education System</h1>
              <p>
                A next-generation AI learning platform that personalizes education,
                supports teachers, automates assessments, and turns classroom activity
                into actionable intelligence.
              </p>
              <div className="vidya-hero-actions">
                <Link to="/scheduleDemo" className="vidya-btn vidya-btn-primary">
                  <PlayCircle size={19} />
                  Schedule Demo
                </Link>
                <a href="#vidya-capabilities" className="vidya-btn vidya-btn-secondary">
                  <Sparkles size={18} />
                  Explore AI Capabilities
                </a>
              </div>
            </div>

            <div className="vidya-command-panel" aria-label="Vidya learning intelligence preview">
              <div className="vidya-panel-header">
                <span></span>
                <strong>Learning Intelligence Live</strong>
              </div>
              <div className="vidya-student-card">
                <div>
                  <span className="vidya-mini-label">Current focus</span>
                  <h2>Fractions and ratios</h2>
                </div>
                <div className="vidya-progress-ring">
                  <span>82%</span>
                </div>
              </div>
              <div className="vidya-signal-list">
                {learningSignals.map((signal, index) => (
                  <div className="vidya-signal" key={signal}>
                    <CheckCircle2 size={17} />
                    <span>{signal}</span>
                    <small>{index < 3 ? 'Ready' : 'Live'}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="container">
            <div className="vidya-hero-stats">
              {heroStats.map((stat) => (
                <div key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="vidya-section vidya-intro">
          <div className="container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-5">
                <span className="vidya-eyebrow">Built for modern education</span>
                <h2>One AI layer for learning, teaching, assessment, and improvement.</h2>
              </div>
              <div className="col-lg-7">
                <p>
                  Vidya brings together adaptive learning, intelligent tutoring, automated
                  evaluation, and academic analytics so institutions can deliver more
                  responsive, measurable, and engaging learning experiences.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="vidya-section" id="vidya-capabilities">
          <div className="container">
            <div className="vidya-section-heading">
              <span className="vidya-eyebrow">Core capabilities</span>
              <h2>AI workflows that feel useful in real classrooms.</h2>
            </div>
            <div className="row g-4">
              {capabilities.map((item) => {
                const Icon = item.icon;
                return (
                  <div className="col-md-6 col-xl-3" key={item.title}>
                    <article className="vidya-feature-card">
                      <div className="vidya-feature-icon">
                        <Icon size={25} />
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="vidya-section vidya-workflow-band">
          <div className="container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-5">
                <span className="vidya-eyebrow">Learning loop</span>
                <h2>From student activity to smarter instruction.</h2>
                <p>
                  Vidya continuously converts learner behavior into recommendations,
                  tutor support, assessment feedback, and teacher-facing insight.
                </p>
                <Link to="/scheduleDemo" className="vidya-inline-link">
                  See Vidya in action <ArrowRight size={18} />
                </Link>
              </div>
              <div className="col-lg-7">
                <div className="vidya-workflow-grid">
                  {workflow.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <article className="vidya-workflow-step" key={step.title}>
                        <span>{String(index + 1).padStart(2, '0')}</span>
                        <Icon size={28} />
                        <h3>{step.title}</h3>
                        <p>{step.text}</p>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="vidya-section">
          <div className="container">
            <div className="vidya-section-heading">
              <span className="vidya-eyebrow">Designed for everyone</span>
              <h2>One platform, three powerful experiences.</h2>
            </div>
            <div className="row g-4">
              {roles.map((role) => {
                const Icon = role.icon;
                return (
                  <div className="col-md-4" key={role.title}>
                    <article className="vidya-role-card">
                      <Icon size={32} />
                      <h3>{role.title}</h3>
                      <p>{role.text}</p>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="vidya-section vidya-outcomes">
          <div className="container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-5">
                <span className="vidya-eyebrow">Platform outcomes</span>
                <h2>Make learning more personal, measurable, and scalable.</h2>
                <p>
                  Vidya helps education teams move from generic delivery to responsive
                  learning operations backed by AI, analytics, and automation.
                </p>
              </div>
              <div className="col-lg-7">
                <div className="vidya-outcome-list">
                  {outcomes.map((outcome) => (
                    <div className="vidya-outcome-item" key={outcome}>
                      <CheckCircle2 size={19} />
                      <span>{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="vidya-cta">
          <div className="container">
            <div className="vidya-cta-inner">
              <div>
                <span className="vidya-eyebrow">Ready for an AI-powered classroom?</span>
                <h2>Bring Vidya into your learning ecosystem.</h2>
                <p>
                  Explore how Vidya can support personalized education, teacher productivity,
                  and institution-wide learning intelligence.
                </p>
              </div>
              <div className="vidya-cta-actions">
                <Link to="/scheduleDemo" className="vidya-btn vidya-btn-primary">
                  <PlayCircle size={19} />
                  Schedule Demo
                </Link>
                <Link to="/contact" className="vidya-btn vidya-btn-dark">
                  <LockKeyhole size={18} />
                  Talk to Team
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Thank />
      <BottomContent />
      <DarkModeButton />
      <ContactButton />
      <ScrollButton />
    </>
  );
}
